import React, { FC, useState } from "react";
import clsx from "clsx";
import { Modal, ModalBody } from "reactstrap";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "../december-doors.module.scss";

const STEPS = ["step1", "step2", "step3"] as const;

interface WelcomeTourProps {
  isOpen: boolean;
  /** Today's door number while it is still shut; the last step opens it. */
  todayDay: number | null;
  isReducedMotion: boolean;
  onDismiss: () => void;
  onStart: () => void;
}

const WelcomeTour: FC<WelcomeTourProps> = ({
  isOpen,
  todayDay,
  isReducedMotion,
  onDismiss,
  onStart,
}) => {
  const { t } = useTranslation(["decemberDoors"]);
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  const primaryLabel = () => {
    if (!isLastStep) return t("decemberDoors:tour.next");
    if (todayDay === null) return t("decemberDoors:tour.close");
    return t("decemberDoors:tour.start", { day: todayDay });
  };

  const handlePrimary = () => {
    if (!isLastStep) {
      setStepIndex(stepIndex + 1);
      return;
    }
    if (todayDay === null) {
      onDismiss();
      return;
    }
    onStart();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onDismiss}
      centered
      fade={!isReducedMotion}
      className={styles.tour}
      contentClassName={styles["tour-content"]}
      labelledBy="december-doors-tour-title"
    >
      <ModalBody className={styles["tour-body"]} data-testid="doors-tour">
        <div
          key={step}
          className={clsx(
            styles["tour-step"],
            !isReducedMotion && styles["tour-step-animated"]
          )}
        >
          <h2 id="december-doors-tour-title" className={styles["tour-title"]}>
            {t(`decemberDoors:tour.${step}.title`, { day: todayDay })}
          </h2>
          <p className={styles["tour-text"]}>
            {t(`decemberDoors:tour.${step}.body`, { day: todayDay })}
          </p>
        </div>
        <div className={styles["tour-footer"]}>
          <span className={styles["tour-dots"]} aria-hidden>
            {STEPS.map((name, index) => (
              <span
                key={name}
                className={clsx(
                  styles["tour-dot"],
                  index === stepIndex && styles["tour-dot-current"]
                )}
              />
            ))}
          </span>
          <span className={styles["tour-actions"]}>
            {!isLastStep && (
              <button
                type="button"
                className={styles["tour-skip"]}
                onClick={onDismiss}
              >
                {t("decemberDoors:tour.skip")}
              </button>
            )}
            <button
              type="button"
              className={styles["tour-primary"]}
              onClick={handlePrimary}
              data-testid="doors-tour-primary"
            >
              {primaryLabel()}
            </button>
          </span>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default WelcomeTour;
